/**
 * Where an achiever's notification click should land.
 *
 * The backend's `actionUrl` is a coarse, role-agnostic string ("/pab/{id}",
 * "/sessions", "/goals") that predates the Journey Portfolio. On the achiever
 * side every one of those destinations now lives as a tab inside a journey
 * detail page -- `/pab/{programId}/details?tab=…` for a PAB,
 * `/programs/{externalId}/journey?tab=…` for an Expert Program -- so the
 * destination is derived here from the notification type plus the program the
 * notification belongs to, and `actionUrl` is only read as a last-resort
 * source for the program id.
 *
 * Session-specific notifications don't navigate at all: they open the same
 * Session Prep / Session Summary drawers the journey Sessions tab opens,
 * addressed by the `meeting_id` the backend puts in `metadataJson`.
 */
import type { Notification, ProgramSummary } from "@/services/api";
import type { JourneyTabId } from "@/pages/journey/components/JourneyOverviewWidgets";

/** Handled by the MessagesListDrawer, not by navigation. */
export const MESSAGE_NOTIFICATION_TYPES = new Set(["new_direct_message", "unread_messages_digest"]);

/** Opens the Session Prep drawer (Briefing Artifact / Goals / Agenda). */
const SESSION_PREP_DRAWER_TYPES = new Set([
  "session_prep_ready",
  "session_prep_reminder_24h",
  "prep_notes_reminder",
  "prep_notes_submitted",
]);

/**
 * Organisation program notifications. Their metadata names the program by
 * external id only (`program_external_id`), and an org cycle has its own page
 * and tabs rather than journey tabs -- so they open that page, the sessions
 * ones on its Sessions tab. The invitation is answered on Home.
 */
const ORG_PROGRAM_TAB_BY_TYPE: Record<string, string | null> = {
  // Added to the program, or given a North Star / OKR / OKI.
  org_program_added: null,
  org_invite_accepted: null,
  org_invite_declined: null,
  org_session_removed: "sessions",
  org_session_cancelled: "sessions",
};

/** Opens the Session Summary drawer -- these all follow a session that ran. */
const SESSION_SUMMARY_DRAWER_TYPES = new Set(["session_feedback_request"]);

/** Which journey tab each notification type belongs to. */
const TAB_BY_NOTIFICATION_TYPE: Record<string, JourneyTabId> = {
  // Goals & tasks
  goal_achieved: "goals",
  goal_achieved_celebration: "goals",
  first_goal_achieved: "goals",
  advisor_tagged_in_goal: "goals",
  task_overdue: "goals",
  // The board
  pab_formed: "advisors",
  pab_fully_formed: "advisors",
  advisor_accepted_invite: "advisors",
  advisor_declined_invite: "advisors",
  board_invitation_accepted: "advisors",
  board_invitation_declined: "advisors",
  // Portfolio
  program_graduation: "stars",
  // Sessions -- the fallback for the drawer types above when the notification
  // predates `meeting_id` metadata and there is nothing to open a drawer with.
  meeting_scheduled: "sessions",
  session_booking_confirmed: "sessions",
  group_session_invitation: "sessions",
  session_cancelled_by_advisor: "sessions",
  session_cancelled_by_achiever: "sessions",
  session_rescheduled: "sessions",
  session_starting_soon: "sessions",
  session_feedback_request: "sessions",
  session_prep_ready: "sessions",
  session_prep_reminder_24h: "sessions",
  prep_notes_reminder: "sessions",
  prep_notes_submitted: "sessions",
};

export type AchieverNotificationTarget =
  | { kind: "messages" }
  | { kind: "session-prep"; meetingId: number; programId: number | null }
  | { kind: "session-summary"; meetingId: number; programId: number | null }
  | { kind: "route"; path: string }
  | { kind: "none" };

const parseMetadata = (metadataJson?: string | null): Record<string, unknown> => {
  if (!metadataJson) return {};
  try {
    const parsed: unknown = JSON.parse(metadataJson);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

const toId = (raw: unknown): number | null => {
  if (raw === undefined || raw === null || raw === "") return null;
  const id = typeof raw === "number" ? raw : parseInt(String(raw), 10);
  return isNaN(id) ? null : id;
};

/**
 * Session notifications carry the meeting in `metadataJson`
 * (backend: `queue_notification(..., metadata={"meeting_id": …})`), never in
 * `actionUrl` -- that field is the static string "/sessions" for these types.
 */
export const extractMeetingId = (metadataJson?: string | null): number | null =>
  toId(parseMetadata(metadataJson).meeting_id);

/**
 * The goal/task a goals-tab notification is about, as a URL hash the journey
 * page scrolls to (see useScrollToHash). Task wins over goal when both are
 * present -- OkrCard renders `okt-{id}` on the task row inside `okr-{id}`, so
 * the narrower anchor still lands inside the right goal card.
 */
const goalAnchor = (notification: Notification): string => {
  const meta = parseMetadata(notification.metadataJson);
  const taskId = toId(meta.okt_id) ?? toId(meta.task_id);
  if (taskId !== null) return `#okt-${taskId}`;
  const okrId = toId(meta.okr_id) ?? toId(meta.goal_id);
  return okrId !== null ? `#okr-${okrId}` : "";
};

/**
 * The program (PAB or Expert) this notification belongs to. `program_id` is
 * the canonical metadata key; `session_id` / `pab_session_id` are the older
 * aliases some trigger sites still use, and `/pab/{id}` in `actionUrl` is the
 * fallback for notification rows created before any of them were written.
 *
 * v3.1 note: a "pab session id" IS a program id -- same number, two names.
 */
export const extractProgramId = (notification: Notification): number | null => {
  const meta = parseMetadata(notification.metadataJson);
  const fromMeta = toId(meta.program_id) ?? toId(meta.session_id) ?? toId(meta.pab_session_id);
  if (fromMeta !== null) return fromMeta;
  const match = /^\/pab\/(\d+)/.exec(notification.actionUrl ?? "");
  return match ? toId(match[1]) : null;
};

/** `/pab/{id}/details?tab=…` or `/programs/{externalId}/journey?tab=…`, matching
 *  JourneyPortfolioPage's own primary action for the journey. */
export const journeyTabPath = (
  program: ProgramSummary | undefined,
  programId: number,
  tab: JourneyTabId,
): string => {
  if (program?.programType === "EXPERT")
    return `/programs/${program.externalId}/journey?tab=${tab}`;
  // An org cycle has its own page and tabs; the journey tab ids do not apply.
  if (program?.programType === "ORGANISATION")
    return `/organisation-programs/${program.externalId}`;
  return `/pab/${programId}/details?tab=${tab}`;
};

/**
 * `programsById` is the achiever's own journeys, keyed by program id, from
 * `GET /programs` (`programsApi.list`). A PAB that has already been archived
 * drops out of that response, but the PAB route only needs the numeric id, so
 * a miss is treated as a PAB rather than a failure.
 */
export const resolveAchieverNotificationTarget = (
  notification: Notification,
  programsById: Map<number, ProgramSummary>,
  options?: {
    /** Program id resolved by the caller (e.g. by asking the API which program
     *  owns this notification's meeting). Takes precedence over the guesses
     *  below, but not over an id the notification carries itself. */
    programIdFallback?: number | null;
  },
): AchieverNotificationTarget => {
  if (!notification.isActionable) return { kind: "none" };

  const type = notification.notificationType;
  if (MESSAGE_NOTIFICATION_TYPES.has(type)) return { kind: "messages" };

  // Organisation programs: straight to the program's page -- never a guessed
  // journey, which is where these used to land (they carry no `program_id`).
  if (type === "org_program_invited") return { kind: "route", path: "/home" };
  if (type in ORG_PROGRAM_TAB_BY_TYPE) {
    const externalId = parseMetadata(notification.metadataJson).program_external_id;
    if (typeof externalId === "string" && externalId) {
      const tab = ORG_PROGRAM_TAB_BY_TYPE[type];
      return {
        kind: "route",
        path: `/organisation-programs/${externalId}${tab ? `?tab=${tab}` : ""}`,
      };
    }
    // No program named: the server's own actionUrl is the best guess.
    if (notification.actionUrl) return { kind: "route", path: notification.actionUrl };
  }

  const meetingId = extractMeetingId(notification.metadataJson);
  // Notifications queued before program_id was added to their metadata carry no
  // program at all. Two guesses beat dumping them on the portfolio: the
  // achiever's only journey, or -- when they hold several -- their only PAB,
  // since every id-less notification predates Expert Program journeys.
  const programs = [...programsById.values()];
  const soleProgram = programs.length === 1 ? programs[0] : undefined;
  const pabs = programs.filter((p) => p.programType === "PAB");
  const solePab = pabs.length === 1 ? pabs[0] : undefined;
  const programId =
    extractProgramId(notification) ??
    options?.programIdFallback ??
    soleProgram?.id ??
    solePab?.id ??
    null;

  if (meetingId !== null && SESSION_PREP_DRAWER_TYPES.has(type)) {
    return { kind: "session-prep", meetingId, programId };
  }
  if (meetingId !== null && SESSION_SUMMARY_DRAWER_TYPES.has(type)) {
    return { kind: "session-summary", meetingId, programId };
  }

  // Still no program -- the achiever has several journeys and this notification
  // names none of them, so the portfolio is the closest useful landing spot.
  if (programId === null) return { kind: "route", path: "/journey-portfolio" };

  const tab = TAB_BY_NOTIFICATION_TYPE[type] ?? "overview";
  const anchor = tab === "goals" ? goalAnchor(notification) : "";
  return {
    kind: "route",
    path: `${journeyTabPath(programsById.get(programId), programId, tab)}${anchor}`,
  };
};
